import { fromMinorUnits } from '@/utils/currency/from-minor-units';
import { toMinorUnits } from '@/utils/currency/to-minor-units';
import {
  eachMonthOfInterval,
  eachYearOfInterval,
  format,
  max,
  min,
  parseISO,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
  UTCDate,
} from '@/utils/dates';
import { Currency } from '@seapoint/server/enums';
import { PortfolioValuationType } from 'PublicEntityTypes';

const DECIMAL_PLACES = 2;
export const MIN_MONTHS = 6;
export const MIN_YEARS = 6;

const DATE_FORMAT_MONTH = 'yyyy-MM';
const DATE_FORMAT_MONTH_LABEL = 'LLLL';
const DATE_FORMAT_YEAR = 'yyyy';

type ValuationWithDate = {
  valuation: PortfolioValuationType;
  parsedDate: Date;
};

type MonthlyReturn = {
  period: string; // 'YYYY-MM' format
  label: string; // 'November'
  totalReturn: number;
  totalFees: number;
};

type YearlyReturn = {
  period: string; // 'YYYY' format
  totalReturn: number;
  totalFees: number;
};

export type ReturnData = {
  hasValuations: boolean;
  monthlyReturns: MonthlyReturn[];
  yearlyReturns: YearlyReturn[];
  allTimeReturns: number;
  allTimeFees: number;
  currency: Currency;
};

/**
 * Calculates monthly and yearly returns from portfolio valuations
 * - Groups by month/year and sums returns within each period
 * - Fills gaps with 0 returns (both historical gaps and forward to current date)
 * - Returns sorted newest first
 *
 * @example
 * Input: [
 *   { date: '2025-11-01', netReturnAmount: 10 },
 *   { date: '2025-11-15', netReturnAmount: 20 },
 *   { date: '2024-09-01', netReturnAmount: 30 }
 * ]
 * Today: 2025-12-01
 *
 * Output: {
 *   hasValuations: true,
 *   monthlyReturns: [
 *     { period: '2025-12', label: 'December', totalReturn: 0 }, // Forward-looking
 *     { period: '2025-11', label: 'November', totalReturn: 30 },
 *     { period: '2025-10', label: 'October', totalReturn: 0 },  // Gap filled
 *     ...
 *   ],
 *   yearlyReturns: [
 *     { period: '2025', totalReturn: 30 },
 *     { period: '2024', totalReturn: 30 }
 *   ],
 *   allTimeReturns: 60
 * }
 */
export const getReturnData = (portfolioValuation: PortfolioValuationType[], currency: Currency): ReturnData => {
  if (portfolioValuation.length === 0) {
    const today = new UTCDate();

    return {
      hasValuations: false,
      monthlyReturns: [
        {
          period: format(today, DATE_FORMAT_MONTH),
          label: format(today, DATE_FORMAT_MONTH_LABEL),
          totalReturn: 0,
          totalFees: 0,
        },
      ],
      yearlyReturns: [
        {
          period: format(today, DATE_FORMAT_YEAR),
          totalReturn: 0,
          totalFees: 0,
        },
      ],
      allTimeReturns: 0,
      allTimeFees: 0,
      currency,
    };
  }

  // Parse all dates once upfront - prevents future bugs where parseISO might be forgotten
  const valuationsWithDates: ValuationWithDate[] = portfolioValuation.map((valuation) => ({
    valuation,
    parsedDate: parseISO(valuation.date),
  }));

  const monthlyReturnsMap = groupReturnsByMonth(valuationsWithDates);
  const monthlyFeesMap = groupFeesByMonth(valuationsWithDates);
  const allMonths = getAllMonthsInRange(valuationsWithDates);
  const monthlyReturns = buildMonthlyReturns(allMonths, monthlyReturnsMap, monthlyFeesMap);

  const yearlyReturnsMap = groupReturnsByYear(valuationsWithDates);
  const yearlyFeesMap = groupFeesByYear(valuationsWithDates);
  const allYears = getAllYearsInRange(valuationsWithDates);
  const yearlyReturns = buildYearlyReturns(allYears, yearlyReturnsMap, yearlyFeesMap);

  // Calculate all-time totals from raw data
  const allTimeReturns = calculateTotalReturn(portfolioValuation.map((v) => v.netReturnAmount ?? 0));
  const allTimeFees = calculateTotalReturn(portfolioValuation.map((v) => v.dailyFeeAmount ?? 0));

  return {
    hasValuations: true,
    monthlyReturns,
    yearlyReturns,
    allTimeReturns,
    allTimeFees,
    currency,
  };
};

/**
 * Groups portfolio valuations by month (YYYY-MM), collecting return amounts per month
 *
 * @example
 * Input: [
 *   { date: '2025-11-01', netReturnAmount: 10 },
 *   { date: '2025-11-15', netReturnAmount: 20 },
 *   { date: '2025-10-01', netReturnAmount: 30 }
 * ]
 *
 * Output: Map {
 *   '2025-11' => [10, 20],
 *   '2025-10' => [30]
 * }
 */
const groupReturnsByMonth = (valuationsWithDates: ValuationWithDate[]): Map<string, number[]> => {
  const returnsMap = new Map<string, number[]>();

  valuationsWithDates.forEach(({ valuation, parsedDate }) => {
    const monthKey = format(parsedDate, DATE_FORMAT_MONTH);

    const amounts = returnsMap.get(monthKey);

    if (!amounts) {
      returnsMap.set(monthKey, [valuation.netReturnAmount ?? 0]);
    } else {
      amounts.push(valuation.netReturnAmount ?? 0);
    }
  });

  return returnsMap;
};

/**
 * Groups portfolio valuations by month (YYYY-MM), collecting fee amounts per month
 *
 * @example
 * Input: [
 *   { date: '2025-11-01', dailyFeeAmount: 0.5 },
 *   { date: '2025-11-15', dailyFeeAmount: 0.6 },
 *   { date: '2025-10-01', dailyFeeAmount: 0.4 }
 * ]
 *
 * Output: Map {
 *   '2025-11' => [0.5, 0.6],
 *   '2025-10' => [0.4]
 * }
 */
const groupFeesByMonth = (valuationsWithDates: ValuationWithDate[]): Map<string, number[]> => {
  const monthlyFeesMap = new Map<string, number[]>();

  valuationsWithDates.forEach(({ valuation, parsedDate }) => {
    const monthKey = format(parsedDate, DATE_FORMAT_MONTH);

    const amounts = monthlyFeesMap.get(monthKey);

    if (!amounts) {
      monthlyFeesMap.set(monthKey, [valuation.dailyFeeAmount ?? 0]);
    } else {
      amounts.push(valuation.dailyFeeAmount ?? 0);
    }
  });

  return monthlyFeesMap;
};

/**
 * Generates all months from earliest valuation date to current date (whichever is later)
 * Extends to today if it's later than latest valuation (forward-looking gap filling)
 *
 * @example
 * Input: [
 *   { valuation: { date: '2025-11-15', ... }, parsedDate: Date(...) },
 *   { valuation: { date: '2025-09-01', ... }, parsedDate: Date(...) }
 * ]
 * Today: 2025-12-01
 *
 * Output: [
 *   Date('2025-09-01'), // Earliest
 *   Date('2025-10-01'), // Gap filled
 *   Date('2025-11-01'), // Latest valuation
 *   Date('2025-12-01')  // Current month (forward-looking)
 * ]
 */
const getAllMonthsInRange = (valuationsWithDates: ValuationWithDate[]): Date[] => {
  const dates = valuationsWithDates.map(({ parsedDate }) => parsedDate);

  const earliestValuationDate = startOfMonth(min(dates));
  const latestValuationDate = startOfMonth(max(dates));
  const today = startOfMonth(new UTCDate());

  // Extend to today if it's later than latest valuation
  const latestDate = max([latestValuationDate, today]);

  // Ensure at least MIN_MONTHS data points by extending backwards if needed
  const minStartForMinMonths = subMonths(latestDate, MIN_MONTHS - 1);
  const earliestDate = min([earliestValuationDate, minStartForMinMonths]);

  return eachMonthOfInterval({ start: earliestDate, end: latestDate });
};

/**
 * Builds monthly return objects with totals, filling gaps with 0
 * Returns sorted newest first
 *
 * @example
 * Input:
 *   allMonths: [Date('2025-09-01'), Date('2025-10-01'), Date('2025-11-01')]
 *   monthlyReturnsMap: Map { '2025-11' => [10, 20], '2025-09' => [30] }
 *   monthlyFeesMap: Map { '2025-11' => [0.5, 0.6], '2025-09' => [0.4] }
 *
 * Output: [
 *   { period: '2025-11', label: 'November', totalReturn: 30, totalFees: 1.1 },
 *   { period: '2025-10', label: 'October', totalReturn: 0, totalFees: 0 },   // Gap filled
 *   { period: '2025-09', label: 'September', totalReturn: 30, totalFees: 0.4 }
 * ]
 */
const buildMonthlyReturns = (
  allMonths: Date[],
  monthlyReturnsMap: Map<string, number[]>,
  monthlyFeesMap: Map<string, number[]>,
): MonthlyReturn[] => {
  const monthlyReturns: MonthlyReturn[] = allMonths.map((monthDate) => {
    const monthKey = format(monthDate, DATE_FORMAT_MONTH);
    const returnsForMonth = monthlyReturnsMap.get(monthKey) ?? [];
    const feesForMonth = monthlyFeesMap.get(monthKey) ?? [];

    return {
      period: monthKey,
      label: format(monthDate, DATE_FORMAT_MONTH_LABEL),
      totalReturn: calculateTotalReturn(returnsForMonth),
      totalFees: calculateTotalReturn(feesForMonth),
    };
  });

  // Sort by period descending (newest first: 2025-11 > 2025-10 > 2025-09)
  monthlyReturns.sort((a, b) => b.period.localeCompare(a.period));

  return monthlyReturns;
};

/**
 * Sums monetary amounts using integer arithmetic to avoid floating-point errors
 *
 * @example
 * calculateTotalReturn([10.5, 20.3, 5.2]) // 36.0
 * calculateTotalReturn([0.1, 0.2])        // 0.3 (not 0.30000000000000004)
 * calculateTotalReturn([])                // 0
 */
const calculateTotalReturn = (amounts: number[]): number => {
  const totalInCents = amounts.reduce((acc, amount) => acc + toMinorUnits(amount, DECIMAL_PLACES), 0);

  return fromMinorUnits(totalInCents, DECIMAL_PLACES);
};

/**
 * Groups portfolio valuations by year (YYYY), collecting return amounts per year
 *
 * @example
 * Input: [
 *   { date: '2025-11-01', netReturnAmount: 10 },
 *   { date: '2025-03-15', netReturnAmount: 20 },
 *   { date: '2024-10-01', netReturnAmount: 30 }
 * ]
 *
 * Output: Map {
 *   '2025' => [10, 20],
 *   '2024' => [30]
 * }
 */
const groupReturnsByYear = (valuationsWithDates: ValuationWithDate[]): Map<string, number[]> => {
  const returnsMap = new Map<string, number[]>();

  valuationsWithDates.forEach(({ valuation, parsedDate }) => {
    const yearKey = format(parsedDate, DATE_FORMAT_YEAR);

    const amounts = returnsMap.get(yearKey);
    if (!amounts) {
      returnsMap.set(yearKey, [valuation.netReturnAmount ?? 0]);
    } else {
      amounts.push(valuation.netReturnAmount ?? 0);
    }
  });

  return returnsMap;
};

/**
 * Groups portfolio valuations by year (YYYY), collecting fee amounts per year
 *
 * @example
 * Input: [
 *   { date: '2025-11-01', dailyFeeAmount: 0.5 },
 *   { date: '2025-03-15', dailyFeeAmount: 0.6 },
 *   { date: '2024-10-01', dailyFeeAmount: 0.4 }
 * ]
 *
 * Output: Map {
 *   '2025' => [0.5, 0.6],
 *   '2024' => [0.4]
 * }
 */
const groupFeesByYear = (valuationsWithDates: ValuationWithDate[]): Map<string, number[]> => {
  const yearlyFeesMap = new Map<string, number[]>();

  valuationsWithDates.forEach(({ valuation, parsedDate }) => {
    const yearKey = format(parsedDate, DATE_FORMAT_YEAR);

    const amounts = yearlyFeesMap.get(yearKey);
    if (!amounts) {
      yearlyFeesMap.set(yearKey, [valuation.dailyFeeAmount ?? 0]);
    } else {
      amounts.push(valuation.dailyFeeAmount ?? 0);
    }
  });

  return yearlyFeesMap;
};

/**
 * Generates all years from earliest valuation date to current year (whichever is later)
 * - Extends to current year if it's later than latest valuation (forward-looking gap filling)
 * - Ensures at least MIN_YEARS (6) data points by extending backwards if needed
 *
 * @example
 * Input: [
 *   { valuation: { date: '2025-11-15', ... }, parsedDate: Date(...) },
 *   { valuation: { date: '2023-09-01', ... }, parsedDate: Date(...) }
 * ]
 * Today: 2026-01-15
 *
 * Output: [
 *   Date('2021-01-01'), // Extended backwards to meet MIN_YEARS
 *   Date('2022-01-01'), // Extended backwards to meet MIN_YEARS
 *   Date('2023-01-01'), // Earliest valuation
 *   Date('2024-01-01'), // Gap filled
 *   Date('2025-01-01'), // Latest valuation
 *   Date('2026-01-01')  // Current year (forward-looking)
 * ]
 */
const getAllYearsInRange = (valuationsWithDates: ValuationWithDate[]): Date[] => {
  const dates = valuationsWithDates.map(({ parsedDate }) => parsedDate);

  const earliestValuationDate = startOfYear(min(dates));
  const latestValuationDate = startOfYear(max(dates));
  const today = startOfYear(new UTCDate());

  // Extend to current year if it's later than latest valuation
  const latestDate = max([latestValuationDate, today]);

  // Ensure at least MIN_YEARS data points by extending backwards if needed
  const minStartForMinYears = subYears(latestDate, MIN_YEARS - 1);
  const earliestDate = min([earliestValuationDate, minStartForMinYears]);

  return eachYearOfInterval({ start: earliestDate, end: latestDate });
};

/**
 * Builds yearly return objects with totals, filling gaps with 0
 * Returns sorted newest first
 *
 * @example
 * Input:
 *   allYears: [Date('2023-01-01'), Date('2024-01-01'), Date('2025-01-01')]
 *   yearlyReturnsMap: Map { '2025' => [10, 20], '2023' => [30] }
 *   yearlyFeesMap: Map { '2025' => [0.5, 0.6], '2023' => [0.4] }
 *
 * Output: [
 *   { period: '2025', totalReturn: 30, totalFees: 1.1 },
 *   { period: '2024', totalReturn: 0, totalFees: 0 },   // Gap filled
 *   { period: '2023', totalReturn: 30, totalFees: 0.4 }
 * ]
 */
const buildYearlyReturns = (
  allYears: Date[],
  yearlyReturnsMap: Map<string, number[]>,
  yearlyFeesMap: Map<string, number[]>,
): YearlyReturn[] => {
  const yearlyReturns: YearlyReturn[] = allYears.map((yearDate) => {
    const yearKey = format(yearDate, DATE_FORMAT_YEAR);
    const returnsForYear = yearlyReturnsMap.get(yearKey) ?? [];
    const feesForYear = yearlyFeesMap.get(yearKey) ?? [];

    return {
      period: yearKey,
      totalReturn: calculateTotalReturn(returnsForYear),
      totalFees: calculateTotalReturn(feesForYear),
    };
  });

  // Sort by period descending (newest first: 2025 > 2024 > 2023)
  yearlyReturns.sort((a, b) => b.period.localeCompare(a.period));

  return yearlyReturns;
};
